import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Clock } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface PendingMatch {
  id: string;
  created_at: string;
  status: string;
  profile_1_id: string;
  profile_2_id: string;
}

interface PendingMatchesCardProps {
  matches: PendingMatch[];
  userId: string;
  onViewMatch: (matchId: string) => void;
}

export function PendingMatchesCard({ matches, userId, onViewMatch }: PendingMatchesCardProps) {
  const getMatchStatus = (match: PendingMatch) => {
    if (match.status === 'pending') return 'New Match';
    if (match.status === 'accepted_by_1' && match.profile_1_id === userId) return 'You Accepted';
    if (match.status === 'accepted_by_2' && match.profile_2_id === userId) return 'You Accepted';
    if (match.status === 'accepted_by_1' && match.profile_2_id === userId) return 'Action Required';
    if (match.status === 'accepted_by_2' && match.profile_1_id === userId) return 'Action Required';
    return 'Pending';
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Pending Matches</CardTitle>
          <Badge variant="secondary">{matches.length}</Badge>
        </div>
        <CardDescription>
          Matches awaiting your response
        </CardDescription>
      </CardHeader>
      <CardContent>
        {matches.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Clock className="h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">No pending matches</p>
            <p className="text-xs text-muted-foreground mt-1">
              New matches will appear here
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {matches.map((match, index) => (
              <div key={match.id}>
                {index > 0 && <Separator className="my-3" />}
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {getMatchStatus(match)}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(match.created_at), { addSuffix: true })}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onViewMatch(match.id)}
                  >
                    View Match
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
